'use client'
import type { Dispatch, FC, ReactNode, SetStateAction } from 'react'
import { createContext, useCallback, useEffect, useMemo, useState } from 'react'
import { usePathname, useSearchParams } from 'next/navigation'
import { E_FILTER_TYPE } from '@/schema'
import { parseSearchParams, searchParamsToActiveFilters } from '@/utils'

export type TypeFilter = {
  id: number
  slug: string
  name: string
  type?: E_FILTER_TYPE
  disabled?: boolean
  // needed for Select only
  parentParam?: string
  parentName?: string
  parentSlug?: string
}

type TypeRawFilters = {
  brands: TypeFilter[]
  [key: string]: TypeFilter[]
}

type TypeActiveFilterValue = {
  name: string
  slug: string
}

export type TypeActiveFilter = {
  param: string,
  value: TypeActiveFilterValue | TypeActiveFilterValue[]
}

type TypeContext = {
  currentFilters: TypeRawFilters,
  setCurrentFilters: Dispatch<SetStateAction<TypeRawFilters>>

  activeFilters: TypeActiveFilter[],
  setActiveFilters: Dispatch<SetStateAction<TypeActiveFilter[]>>

  totalResultsCount?: number
  setTotalResultsCount?: Dispatch<SetStateAction<number>>

  updateUrlWithFilters: (groupedActiveFilters) => void
}

export const FiltersContext = createContext<TypeContext | null>(null)


interface Props {
  children: ReactNode
  initialRawFilters: TypeRawFilters
  initialActiveFilters: TypeContext['activeFilters']
}

const getIncomingParams = (filtersArr, pathname, searchParams) => {
  const incomingParams = new URLSearchParams()
  // Preserve 'q' param on search pages
  if (pathname?.includes('/search')) {
    const qParam = searchParams?.get('q')
    if (qParam) incomingParams.set('q', qParam)
  }

  if (!filtersArr?.length) return incomingParams.toString().replace(/%2C/g, ',')

  for (const filter of filtersArr) {
    const encodedValues = Array.isArray(filter.value) ? filter.value.map(value => encodeURIComponent(value?.slug)).join(',') : filter.value?.slug

    if(encodedValues) incomingParams.set(filter.param, encodedValues)
  }

  return incomingParams.toString().replace(/%2C/g, ',')
}

export const FiltersProvider: FC<Props> = ({ children, initialRawFilters, initialActiveFilters }) => {
  const [_rawFilters, _setRawFilters] = useState<TypeRawFilters>(initialRawFilters ?? null)
  const [currentFilters, setCurrentFilters] = useState<TypeContext['currentFilters']>(initialRawFilters ?? null)
  const [activeFilters, setActiveFilters] = useState<TypeContext['activeFilters']>(initialActiveFilters ?? [])
  const [totalResultsCount, setTotalResultsCount] = useState<TypeContext['totalResultsCount']>(null)

  const pathname = usePathname()
  const searchParams = useSearchParams()

  const updateUrlWithFilters = useCallback((filtersArr) => {
    const incomingParamsAsString = getIncomingParams(filtersArr, pathname, searchParams)
    const newUrl = `${pathname}${incomingParamsAsString ? `?${incomingParamsAsString}` : ''}`

    // Push new URL so the user can use the back button
    window.history.pushState(null, '', newUrl)
  }, [pathname, searchParams])

  // Sync activeFilters from URL when user uses browser back/forward
  useEffect(() => {
    const handlePopState = () => {
      const searchString = window.location.search
      if (!searchString) {
        setActiveFilters([])
        return
      }
      const parsed = parseSearchParams(searchString.slice(1))
      setActiveFilters(searchParamsToActiveFilters(parsed))
    }

    window.addEventListener('popstate', handlePopState)
    return () => window.removeEventListener('popstate', handlePopState)
  }, [setActiveFilters])

  const contextValue = useMemo(() => ({
    currentFilters,
    setCurrentFilters,
    activeFilters,
    setActiveFilters,
    totalResultsCount,
    setTotalResultsCount,
    updateUrlWithFilters
  }), [
    currentFilters,
    setCurrentFilters,
    activeFilters,
    setActiveFilters,
    totalResultsCount,
    setTotalResultsCount,
    updateUrlWithFilters
  ])

  return (
    <FiltersContext.Provider value={contextValue}>
      {children}
    </FiltersContext.Provider>
  )
}
